import React from 'react';
import { AppLoadingState } from './AppLoadingState';
import { notificationApi } from '../services/api';

type OrgNotificationUnit = {
  id: string;
  name?: string | null;
  slug?: string | null;
};

type OrgNotificationAttachment = {
  id: string;
  name?: string | null;
  type?: string | null;
  size?: number | null;
  downloadPath?: string | null;
};

type OrgNotificationUnitDelivery = {
  id: string;
  organizationName?: string | null;
  organizationSlug?: string | null;
  recipientCount?: number | null;
  readCount?: number | null;
  openedCount?: number | null;
  unreadCount?: number | null;
  dismissedCount?: number | null;
  attachmentCount?: number | null;
  status?: string | null;
  attachments?: OrgNotificationAttachment[];
};

type OrgNotificationCampaign = {
  id: string;
  audience?: string | null;
  targetMode?: string | null;
  title?: string | null;
  body?: string | null;
  severity?: string | null;
  status?: string | null;
  targetSummary?: string | null;
  unitCount?: number | null;
  recipientCount?: number | null;
  readCount?: number | null;
  openedCount?: number | null;
  unreadCount?: number | null;
  dismissedCount?: number | null;
  attachmentCount?: number | null;
  attachments?: OrgNotificationAttachment[];
  reach?: {
    recipientCount?: number | null;
    readCount?: number | null;
    openedCount?: number | null;
    unreadCount?: number | null;
    dismissedCount?: number | null;
  };
  createdByName?: string | null;
  expiresAt?: string | Date | null;
  publishedAt?: string | Date | null;
  createdAt?: string | Date | null;
  units?: OrgNotificationUnitDelivery[];
};

type Props = {
  styles: any;
  units: OrgNotificationUnit[];
  unitsLoading: boolean;
  campaigns: OrgNotificationCampaign[];
  campaignsLoading: boolean;
  formatDateTime: (value?: string | Date | null) => string | null;
  onReload: () => void | Promise<void>;
  onCreateNotification: () => void;
};

function unitLabel(unit: OrgNotificationUnit) {
  return unit.name || unit.slug || unit.id;
}

function formatAttachmentSize(size?: number | null) {
  const value = Number(size || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

function canPreviewAttachment(attachment?: OrgNotificationAttachment | null) {
  const type = String(attachment?.type || '').toLowerCase();
  const name = String(attachment?.name || '').toLowerCase();
  return type.startsWith('image/') || type === 'application/pdf' || name.endsWith('.pdf');
}

export function TechDashboardOrgNotificationsTab({
  styles,
  units,
  unitsLoading,
  campaigns,
  campaignsLoading,
  formatDateTime,
  onReload,
  onCreateNotification
}: Props) {
  const pageCardStyle = styles.workbenchCard || styles.card || styles.messagesSection;
  const sectionCardStyle = styles.messagesSection || styles.card;
  const headerActionRowStyle = {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end'
  };
  const secondaryButtonStyle = {
    ...(styles.button || {}),
    ...(styles.secondaryButton || {})
  };
  const primaryButtonStyle = {
    ...(styles.button || {}),
    ...(styles.primaryButton || {})
  };
  const [selectedCampaign, setSelectedCampaign] = React.useState<OrgNotificationCampaign | null>(null);
  const [campaignDetailLoading, setCampaignDetailLoading] = React.useState(false);
  const [campaignDetailError, setCampaignDetailError] = React.useState('');
  const [attachmentPreview, setAttachmentPreview] = React.useState<{
    id: string;
    name: string;
    type: string;
    url: string;
  } | null>(null);
  const [attachmentPreviewLoadingId, setAttachmentPreviewLoadingId] = React.useState('');
  const [attachmentPreviewError, setAttachmentPreviewError] = React.useState('');
  const summaryGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.25rem'
  };
  const summaryCardStyle = {
    ...(styles.statCard || {}),
    padding: '1rem',
    border: '1px solid #d8cfc1',
    borderRadius: '0.85rem',
    background: '#fffdf8'
  };
  const unitChipWrapStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem'
  };
  const unitChipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    border: '1px solid #bfd1cc',
    borderRadius: '999px',
    background: '#eef6f3',
    color: '#063d43',
    padding: '0.35rem 0.7rem',
    fontWeight: 700,
    fontSize: '0.88rem'
  };
  const modalCardStyle = {
    ...(styles.modalContentWide || {}),
    ...(styles.messagesModalCard || {}),
    maxWidth: '980px'
  };
  const modalHeaderStyle = styles.messagesModalHeader || styles.modalHeader;
  const modalTitleStyle = styles.messagesComposeTitle || styles.modalTitle;
  const modalDescriptionStyle = styles.messagesComposeSubtitle || styles.modalHelper;
  const closeButtonStyle = styles.messagesModalCloseButton || { ...(styles.button || {}), ...(styles.secondaryButton || {}) };
  const secondaryButtonStyleCompact = {
    ...(styles.button || {}),
    ...(styles.secondaryButton || {}),
    padding: '0.55rem 0.85rem'
  };
  const reachGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '0.75rem'
  };
  const detailSectionStyle = {
    ...(styles.messagesSection || styles.card || {}),
    marginTop: '1rem'
  };

  React.useEffect(() => {
    return () => {
      if (attachmentPreview?.url) {
        URL.revokeObjectURL(attachmentPreview.url);
      }
    };
  }, [attachmentPreview?.url]);

  const openCampaignDetail = async (campaign: OrgNotificationCampaign) => {
    setSelectedCampaign(campaign);
    setCampaignDetailLoading(true);
    setCampaignDetailError('');
    setAttachmentPreview(null);
    setAttachmentPreviewError('');
    try {
      const detail = await notificationApi.getOrgCampaign(campaign.id);
      setSelectedCampaign(detail);
    } catch (err: any) {
      setCampaignDetailError(err?.message || 'Failed to load ORG notification campaign.');
    } finally {
      setCampaignDetailLoading(false);
    }
  };

  const closeCampaignDetail = () => {
    setSelectedCampaign(null);
    setCampaignDetailError('');
    setCampaignDetailLoading(false);
    setAttachmentPreview(null);
    setAttachmentPreviewError('');
    setAttachmentPreviewLoadingId('');
  };

  const previewAttachment = async (attachment: OrgNotificationAttachment) => {
    if (!selectedCampaign?.id || !attachment?.id) return;
    setAttachmentPreviewLoadingId(attachment.id);
    setAttachmentPreviewError('');
    try {
      const file = await notificationApi.fetchOrgCampaignAttachment(selectedCampaign.id, attachment.id);
      setAttachmentPreview({
        id: attachment.id,
        name: attachment.name || 'Attachment',
        type: file.contentType || attachment.type || 'application/octet-stream',
        url: file.url
      });
    } catch (err: any) {
      setAttachmentPreviewError(err?.message || 'Failed to preview attachment.');
    } finally {
      setAttachmentPreviewLoadingId('');
    }
  };

  const openAttachmentExternally = async (attachment: OrgNotificationAttachment) => {
    if (!selectedCampaign?.id || !attachment?.id) return;
    try {
      await notificationApi.openOrgCampaignAttachment(selectedCampaign.id, attachment.id, attachment.name || undefined);
    } catch (err: any) {
      setAttachmentPreviewError(err?.message || 'Failed to open attachment.');
    }
  };

  return (
    <>
      <div style={styles.header}>
        <div style={styles.headerTitleGroup}>
          <h1 style={styles.pageTitle}>Notifications</h1>
          <div style={styles.pageSubtitle}>Send ORG-level notices to selected connected units.</div>
        </div>
        <div style={headerActionRowStyle}>
          <button type="button" style={secondaryButtonStyle} onClick={onReload}>
            Reload
          </button>
          <button type="button" style={primaryButtonStyle} onClick={onCreateNotification}>
            New ORG notification
          </button>
        </div>
      </div>

      <div style={pageCardStyle}>
        <div style={summaryGridStyle}>
          <div style={summaryCardStyle}>
            <div style={styles.statValue || styles.effectiveValue}>{units.length}</div>
            <div style={styles.statLabel || styles.messagesItemMeta}>Active connected units</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={styles.statValue || styles.effectiveValue}>{campaigns.length}</div>
            <div style={styles.statLabel || styles.messagesItemMeta}>ORG campaigns</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={styles.statValue || styles.effectiveValue}>
              {campaigns.reduce((sum, campaign) => sum + Number(campaign.recipientCount || 0), 0)}
            </div>
            <div style={styles.statLabel || styles.messagesItemMeta}>Total recipients sent</div>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <div style={styles.messagesSectionHeader}>
            <div>
              <div style={styles.messagesPanelTitle}>Connected units</div>
              <div style={styles.messagesPanelSubtitle}>Only active units are eligible for ORG notifications.</div>
            </div>
          </div>
          {unitsLoading ? (
            <AppLoadingState title="Loading units" message="Checking connected ORG units." layout="rows" density="section" />
          ) : units.length === 0 ? (
            <div style={styles.messagesEmpty}>No connected units are available for ORG notifications.</div>
          ) : (
            <div style={unitChipWrapStyle}>
              {units.map(unit => (
                <span key={unit.id} style={unitChipStyle} title={unit.slug || unit.id}>
                  {unitLabel(unit)}
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={{ ...sectionCardStyle, marginTop: '1.25rem' }}>
          <div style={styles.messagesSectionHeader}>
            <div>
              <div style={styles.messagesPanelTitle}>Recent ORG-sent campaigns</div>
              <div style={styles.messagesPanelSubtitle}>Each row is one parent ORG notification with linked unit deliveries.</div>
            </div>
          </div>
          {campaignsLoading ? (
            <AppLoadingState title="Loading campaigns" message="Fetching recently sent ORG notifications." layout="rows" density="section" />
          ) : campaigns.length === 0 ? (
            <div style={styles.messagesEmpty}>No ORG notifications have been sent yet.</div>
          ) : (
            <div style={styles.messagesListScroll}>
              <div style={styles.messagesList}>
                {campaigns.map(campaign => (
                  <div
                    key={campaign.id}
                    style={{ ...styles.messagesItem, cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => openCampaignDetail(campaign)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openCampaignDetail(campaign);
                      }
                    }}
                  >
                    <div style={styles.messagesItemHeader}>
                      <div>
                        <div style={styles.messagesItemBadgeRow}>
                          <span style={{ ...styles.messagesSenderBadge, ...styles.messagesSenderBadgeInternal }}>
                            {campaign.severity || 'info'}
                          </span>
                          <span style={styles.messagesStateBadge}>{campaign.status || 'published'}</span>
                        </div>
                        <div style={styles.messagesItemTitle}>{campaign.title || 'Notification'}</div>
                      </div>
                      <button
                        type="button"
                        style={secondaryButtonStyleCompact}
                        onClick={(event) => {
                          event.stopPropagation();
                          openCampaignDetail(campaign);
                        }}
                      >
                        Open
                      </button>
                    </div>
                    <div style={styles.messagesItemBody}>{campaign.targetSummary || 'No target summary available.'}</div>
                    <div style={styles.messagesItemMetaRow}>
                      <div style={styles.messagesItemMeta}>
                        {Number(campaign.unitCount || 0)} unit{Number(campaign.unitCount || 0) === 1 ? '' : 's'}
                      </div>
                      <div style={styles.messagesItemMeta}>
                        {Number(campaign.recipientCount || 0)} recipient{Number(campaign.recipientCount || 0) === 1 ? '' : 's'}
                      </div>
                      <div style={styles.messagesItemMeta}>
                        {formatDateTime(campaign.publishedAt || campaign.createdAt) || 'Recently'}
                      </div>
                    </div>
                    {Array.isArray(campaign.units) && campaign.units.length > 0 && (
                      <div style={{ display: 'grid', gap: '0.25rem', marginTop: '0.65rem' }}>
                        {campaign.units.map(unit => (
                          <div key={unit.id} style={styles.messagesItemMeta}>
                            {unit.organizationName || unit.organizationSlug || 'Unit'}: {Number(unit.recipientCount || 0)} recipient
                            {Number(unit.recipientCount || 0) === 1 ? '' : 's'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {selectedCampaign && (
        <div style={styles.modal}>
          <div style={modalCardStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>{selectedCampaign.title || 'ORG notification campaign'}</h2>
                <div style={modalDescriptionStyle}>
                  {formatDateTime(selectedCampaign.publishedAt || selectedCampaign.createdAt) || 'Recently'} |{' '}
                  {selectedCampaign.targetSummary || 'Campaign delivery review'}
                </div>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeCampaignDetail}>
                Close
              </button>
            </div>

            {campaignDetailLoading ? (
              <AppLoadingState title="Loading campaign" message="Fetching live ORG notification reach." layout="rows" density="section" />
            ) : campaignDetailError ? (
              <div style={{ ...styles.modalHelper, color: '#a33b2f' }}>{campaignDetailError}</div>
            ) : (
              <div style={styles.modalForm}>
                <div style={reachGridStyle}>
                  <div style={summaryCardStyle}>
                    <div style={styles.statValue || styles.effectiveValue}>
                      {Number(selectedCampaign.reach?.recipientCount ?? selectedCampaign.recipientCount ?? 0)}
                    </div>
                    <div style={styles.statLabel || styles.messagesItemMeta}>Sent</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={styles.statValue || styles.effectiveValue}>
                      {Number(selectedCampaign.reach?.readCount ?? selectedCampaign.readCount ?? selectedCampaign.openedCount ?? 0)}
                    </div>
                    <div style={styles.statLabel || styles.messagesItemMeta}>Opened</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={styles.statValue || styles.effectiveValue}>
                      {Number(selectedCampaign.reach?.unreadCount ?? selectedCampaign.unreadCount ?? 0)}
                    </div>
                    <div style={styles.statLabel || styles.messagesItemMeta}>Not opened</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={styles.statValue || styles.effectiveValue}>
                      {Number(selectedCampaign.reach?.dismissedCount ?? selectedCampaign.dismissedCount ?? 0)}
                    </div>
                    <div style={styles.statLabel || styles.messagesItemMeta}>Dismissed</div>
                  </div>
                </div>

                <div style={detailSectionStyle}>
                  <div style={styles.messagesPanelTitle}>Notification</div>
                  <div style={styles.messagesItemBadgeRow}>
                    <span style={{ ...styles.messagesSenderBadge, ...styles.messagesSenderBadgeInternal }}>
                      {selectedCampaign.severity || 'info'}
                    </span>
                    <span style={styles.messagesStateBadge}>{selectedCampaign.status || 'published'}</span>
                    {selectedCampaign.audience && <span style={styles.messagesStateBadge}>{selectedCampaign.audience}</span>}
                  </div>
                  <div style={{ ...styles.messagesItemBody, whiteSpace: 'pre-wrap', marginTop: '0.75rem' }}>
                    {selectedCampaign.body || 'No notification body was stored for this campaign.'}
                  </div>
                </div>

                <div style={detailSectionStyle}>
                  <div style={styles.messagesPanelTitle}>Attachments</div>
                  {Array.isArray(selectedCampaign.attachments) && selectedCampaign.attachments.length > 0 ? (
                    <div style={{ display: 'grid', gap: '0.65rem', marginTop: '0.75rem' }}>
                      {selectedCampaign.attachments.map((attachment: OrgNotificationAttachment) => (
                        <div key={attachment.id} style={styles.messagesAttachmentItem || styles.messagesItem}>
                          <div>
                            <strong>{attachment.name || 'Attachment'}</strong>
                            <div style={styles.messagesItemMeta}>
                              {attachment.type || 'file'} | {formatAttachmentSize(attachment.size)}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              style={secondaryButtonStyleCompact}
                              onClick={() => previewAttachment(attachment)}
                              disabled={!canPreviewAttachment(attachment) || attachmentPreviewLoadingId === attachment.id}
                            >
                              {attachmentPreviewLoadingId === attachment.id ? 'Loading...' : 'View in app'}
                            </button>
                            <button
                              type="button"
                              style={secondaryButtonStyleCompact}
                              onClick={() => openAttachmentExternally(attachment)}
                            >
                              Open / download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.messagesEmpty}>No attachments were included with this notification.</div>
                  )}

                  {attachmentPreviewError && (
                    <div style={{ ...styles.modalHelper, color: '#a33b2f', marginTop: '0.75rem' }}>{attachmentPreviewError}</div>
                  )}
                  {attachmentPreview && (
                    <div style={{ ...styles.messagesItem, marginTop: '0.9rem' }}>
                      <div style={styles.messagesItemHeader}>
                        <div>
                          <div style={styles.messagesItemTitle}>{attachmentPreview.name}</div>
                          <div style={styles.messagesItemMeta}>{attachmentPreview.type}</div>
                        </div>
                        <button type="button" style={secondaryButtonStyleCompact} onClick={() => setAttachmentPreview(null)}>
                          Hide preview
                        </button>
                      </div>
                      {attachmentPreview.type.toLowerCase().startsWith('image/') ? (
                        <img
                          src={attachmentPreview.url}
                          alt={attachmentPreview.name}
                          style={{ maxWidth: '100%', maxHeight: '520px', objectFit: 'contain', borderRadius: '0.75rem' }}
                        />
                      ) : (
                        <iframe
                          title={attachmentPreview.name}
                          src={attachmentPreview.url}
                          style={{ width: '100%', minHeight: '560px', border: '1px solid #d8cfc1', borderRadius: '0.75rem' }}
                        />
                      )}
                    </div>
                  )}
                </div>

                <div style={detailSectionStyle}>
                  <div style={styles.messagesPanelTitle}>Reach by unit</div>
                  {Array.isArray(selectedCampaign.units) && selectedCampaign.units.length > 0 ? (
                    <div style={{ display: 'grid', gap: '0.6rem', marginTop: '0.75rem' }}>
                      {selectedCampaign.units.map((unit: OrgNotificationUnitDelivery) => (
                        <div key={unit.id} style={styles.messagesItem}>
                          <div style={styles.messagesItemHeader}>
                            <div>
                              <div style={styles.messagesItemTitle}>{unit.organizationName || unit.organizationSlug || 'Unit'}</div>
                              <div style={styles.messagesItemMeta}>{unit.status || 'published'}</div>
                            </div>
                          </div>
                          <div style={reachGridStyle}>
                            <div style={styles.messagesItemMeta}>
                              <strong>{Number(unit.recipientCount || 0)}</strong> sent
                            </div>
                            <div style={styles.messagesItemMeta}>
                              <strong>{Number(unit.readCount ?? unit.openedCount ?? 0)}</strong> opened
                            </div>
                            <div style={styles.messagesItemMeta}>
                              <strong>{Number(unit.unreadCount || 0)}</strong> not opened
                            </div>
                            <div style={styles.messagesItemMeta}>
                              <strong>{Number(unit.dismissedCount || 0)}</strong> dismissed
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.messagesEmpty}>No unit deliveries are linked to this campaign.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
